import { DecimalPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { NumberFormatterPipe } from 'src/app/_shared/pipe/number.pipe';
import { ToStringPipe } from 'src/app/_shared/pipe/to-string.pipe';
import { AppState } from 'src/app/app.state';
import { OpportunityScannerState } from 'src/app/opportunity-scanner/opportunity-scanner.state';
import { WorkspaceService } from 'src/app/workspace/service/workspace.service';
import { environment } from 'src/environments/environment';
import { GoogleMapConfig } from '../../config/google-map.config';
import { Coordinate, MarkerCoordinate, MultiPolygon, Polygon } from '../../interface/google-map.interface';
// import { GoogleHtmlMarker } from './google-html-marker.class';
import {
  GeoJSONStoreFeatures,
  TerraDraw,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import type { GoogleHtmlMarker } from './google-html-marker.class';

export interface GeometryMapDialogData {
  center: Coordinate;
  zoom?: number;
  hideControls?: boolean;
  title?: string;
  multiPolygon?: MultiPolygon;
  radius?: number;
  markerText?: string;
  markerList?: MarkerCoordinate[];
  hideLegend?: boolean;
}

@Component({
  selector: 'app-geometry-map',
  templateUrl: './geometry-map.component.html',
  styleUrls: ['./geometry-map.component.scss'],
})
export class GeometryMapComponent implements OnChanges, AfterViewInit, OnDestroy {
  upgradeSnackbar: MatSnackBarRef<any>;
  @Input() polygonList: any[] = [];
  activeMode = 'draw';
  private drawnPolygons: google.maps.Polygon[] = [];
  /**
   * Google maps instance
   */
  private _map!: google.maps.Map;

  /**
   * A list of all the polygon overlays on initial load.
   * This reference is used to reset map to it's original state.
   */
  private _overlayList: Array<google.maps.Polygon | google.maps.Circle> = [];

  /**
   * Stores the selected polygon overlay to be deleted
   */
  private _selectedOverlayList?: Array<google.maps.Polygon> = [];

  private GoogleHtmlMarkerClass!: typeof GoogleHtmlMarker;

  /**
   * Stores all the GoogleHtmlMarker instances which can be used to alter or remove a marker later
   */
  private _activeMarkerList?: GoogleHtmlMarker[] = [];

  /**
   * Stores the active marker's info-window instance.
   */
  private _activeInfoWindow: google.maps.InfoWindow | undefined;

  @Input() id: string = Math.round(Math.random() * 10000).toString();
  @Input() center!: Coordinate;
  @Input() zoom?: number = 10;
  @Input() hideControls?: boolean = false;
  @Input() title?: string;
  @Input() multiPolygon?: MultiPolygon;
  @Input() radius?: number | null;
  @Input() markerList?: MarkerCoordinate[];
  @Input() hideLegend?: boolean = false;

  /** Emitted when the View Details button on an info-window is clicked */
  @Output() propertyOpened = new EventEmitter<string>();
  property: any;
  isCMAEnabled: any = false;
  terraDraw: TerraDraw;

  /** Emitted when the Select/Deselect button on an info-window is clicked */
  // @Output() propertySelectionToggled = new EventEmitter<ComparableSelectionEvent>();

  constructor(
    @Inject(MAT_DIALOG_DATA) private dialogData: GeometryMapDialogData,
    public dialogRef: MatDialogRef<GeometryMapComponent>,
    private appState: AppState,
    private snackbar: MatSnackBar,
    public opScanState: OpportunityScannerState,
    private decimalPipe: DecimalPipe,
    private workspaceService: WorkspaceService,
    private toStringPipe: ToStringPipe,
    private numberPipe: NumberFormatterPipe // public propertyState: PropertyState
  ) {
    if (dialogData.center) this.center = dialogData.center;
    if (dialogData.zoom) this.zoom = dialogData.zoom;
    if (typeof dialogData.hideControls == 'boolean') this.hideControls = dialogData.hideControls;
    if (dialogData.multiPolygon) this.multiPolygon = dialogData.multiPolygon;
    if (dialogData.radius) this.radius = dialogData.radius;
    if (dialogData.markerList) this.markerList = dialogData.markerList;
    if (dialogData.hideLegend) this.hideLegend = dialogData.hideLegend;
    this.title = dialogData.title;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this._map) {
      const polygonChanged = changes['multiPolygon'];
      const radiusChanged = changes['radius'];
      if (polygonChanged || radiusChanged) {
        this.deleteOverlay('all');
        if (this.multiPolygon && this.multiPolygon.length > 0) {
          this._addMultiPolygonToMap(this.multiPolygon);
        } else if (
          changes['radius']?.currentValue &&
          changes['radius']?.currentValue != changes['radius']?.previousValue
        ) {
          this._drawCircle();
        }
      }

      if (
        changes['markerList']?.currentValue &&
        changes['markerList']?.currentValue != changes['markerList'].previousValue
      ) {
        this._renderHtmlMarker(this.markerList);
      }
    }
  }

  // async ngAfterViewInit(): Promise<void> {
  //   await this._initGoogleMap();
  //   if (this.radius) {
  //     this._drawCircle();
  //   }

  //   if (this.multiPolygon?.length) {
  //     this._addMultiPolygonToMap(this.multiPolygon);
  //   }

  //   if (this.markerList) {
  //     this._renderHtmlMarker(this.markerList);
  //   }
  // }

  async ngAfterViewInit(): Promise<void> {
    const mapsNamespace = google.maps as typeof google.maps & {
      importLibrary?: (library: 'maps') => Promise<unknown>;
    };

    if (mapsNamespace.importLibrary) {
      await mapsNamespace.importLibrary('maps');
    }

    const markerModule = await import('./google-html-marker.class');
    this.GoogleHtmlMarkerClass = markerModule.GoogleHtmlMarker;

    await this._initGoogleMap();

    if (this.radius) {
      this._drawCircle();
    }

    if (this.multiPolygon?.length) {
      this._addMultiPolygonToMap(this.multiPolygon);
    }

    if (this.markerList) {
      this._renderHtmlMarker(this.markerList);
    }
  }

  ngOnDestroy(): void {
    this.destroyMap();
    // TODO: Remove event listeners
  }

  /**
   * Deletes multiple or all polygon overlays on the map
   * @param type Specify whether to delete selected polygon overlays or all overlays
   */
  deleteOverlay(type: 'selected' | 'all'): void {
    if (type == 'all') {
      this._overlayList.forEach((overlay) => overlay.setMap(null));
      this._overlayList = [];
      this._selectedOverlayList = [];
    } else if (type == 'selected') {
      if (!this._selectedOverlayList?.length) {
        this.openSnackbar('Select at least one polygon to delete', false);
        return;
      }
      this._overlayList = this._overlayList.filter((overlay) => {
        if (this._selectedOverlayList?.includes(overlay as google.maps.Polygon)) {
          overlay.setMap(null);
          this._selectedOverlayList = this._selectedOverlayList.filter((selectedOverlay) => {
            return selectedOverlay != overlay;
          });
          return false;
        }
        return true;
      });
    }
  }

  /**
   * Resets the overlay to initial view as it was on opening of the dialog box.
   */
  resetOverlay(): void {
    this.deleteOverlay('all');
    if (this.multiPolygon?.length) this._addMultiPolygonToMap(this.multiPolygon);
  }

  /**
   * Converts the final overlay lists to MultiPolygon
   * And returns the MultiPolygon to parent after closing the dialog box
   */
  onSubmit(): void {
    const newMultiPolygon: MultiPolygon = [];

    this._overlayList.forEach((polygonOverlay) => {
      newMultiPolygon.push(this._getPolygonVertices(polygonOverlay as google.maps.Polygon));
    });

    this.dialogRef.close(newMultiPolygon.length ? newMultiPolygon : null);
  }

  /**
   * Highlight a marker as if it has been hovered over by mouse.
   * It will help the marker standout from the rest of the markers.
   * @param markerCoordinate Marker coordinate which needs to be highlighted
   * @param highlight boolean. Whether the marker should be highlighted or not
   * @returns void
   */
  public highlightMarker(markerCoordinate: MarkerCoordinate, highlight: boolean): void {
    const marker = this._getRelatedMarker(markerCoordinate);
    if (!marker) return;

    marker.highlight(highlight);
  }

  /**
   * Open the info-window for a marker when called.
   * This is similar to clicking on the marker.
   * @param markerCoordinate Marker coordinate for which the info-window needs to be opened
   * @returns void
   */
  public openMarker(markerCoordinate: MarkerCoordinate) {
    const marker = this._getRelatedMarker(markerCoordinate);
    if (!marker) return;

    this._openInfoWindow(marker);
  }

  onCancel() {
    this.destroyMap();
    this.dialogRef.close();
  }

  destroyMap() {
    if (!this._map) return;

    if (this.terraDraw) {
      this.terraDraw.stop();
    }

    // Clear all map listeners
    google.maps.event.clearInstanceListeners(this._map);

    // Clear overlays if any (circle, markers, etc.)
    // this.circle?.setMap(null);
    // this.markers?.forEach(m => m.setMap(null));

    // Remove map reference
    this._map = null as any;
  }

  /**
   * Initialize the Google map and add a location mark at to the center of the map.
   */
  private async _initGoogleMap() {
    this.isCMAEnabled = this.appState?.companyInfoValue?.appConfig?.cmaReport?.isEnabled;
    this._map = new google.maps.Map(document.getElementById(this.id) as HTMLElement, {
      center: new google.maps.LatLng(
        parseFloat(this.center?.lat?.toString()),
        parseFloat(this.center?.lng?.toString())
      ),
      zoom: this.zoom,
      gestureHandling: 'greedy',
      clickableIcons: true,
      streetViewControl: false,
      zoomControl: true,
      mapTypeControl: false,
      fullscreenControl: true,
      draggableCursor: 'default',
      restriction: {
        latLngBounds: {
          north: 56.108929,
          south: 12.812035,
          east: -59.230099,
          west: -135.189578,
        },
      },
      minZoom: 4,
      maxZoom: 19,
      // mapId: 'DEMO_MAP_ID', // Enabling this does not add styles
      styles: GoogleMapConfig.defaultMapStyle,
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_LEFT,
      } as google.maps.ZoomControlOptions,
    });

    this._map.setOptions({ styles: GoogleMapConfig.defaultMapStyle });
    await new Promise<void>((resolve) => {
      google.maps.event.addListenerOnce(this._map, 'idle', () => {
        return resolve();
      });
    });

    if (!this.hideControls) {
      this._map.setOptions({
        gestureHandling: 'greedy',
        zoomControl: true,
        mapTypeControl: true,
        fullscreenControl: true,
      });

      this._initDrawingManager();
    }
    // new GoogleHtmlMarker(this.center as google.maps.LatLngLiteral, 'subject').setMap(this._map);

    new this.GoogleHtmlMarkerClass(
      this.center as google.maps.LatLngLiteral,
      'subject'
    ).setMap(this._map);

    google.maps.event.addListener(this._map, 'click', () => {
      if (this._activeInfoWindow) this._activeInfoWindow.close();
    });
  }

  /**
   * Draws circle on map with the `center` and `radius` in miles
   */
  private _drawCircle() {
    const cityCircle = new google.maps.Circle({
      strokeColor: '#000000',
      fillColor: '#afafaf',
      strokeWeight: 2,
      strokeOpacity: 0.5,
      map: this._map,
      center: new google.maps.LatLng(
        parseFloat(this.center?.lat?.toString()),
        parseFloat(this.center?.lng?.toString())
      ),
      radius: (this.radius ?? 0) * 1609.344,
    });
    this._overlayList.push(cityCircle);
    const circleBound = cityCircle?.getBounds();
    if (circleBound) {
      this._map.fitBounds(circleBound);
      this._map.setZoom((this._map.getZoom() ?? 18) + 0.5);
    }
  }

  /**
   * Initiates Terra Draw instance if `hideControl` is set to false.
   * This helps with drawing polygons on map
   */
  private _initDrawingManager() {
    this.terraDraw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        map: this._map,
        lib: google.maps,
        coordinatePrecision: 9,
      }),
      modes: [
        new TerraDrawSelectMode(),
        new TerraDrawPolygonMode({
          editable: true,
          styles: { fillColor: '#000000', fillOpacity: 0.4, outlineWidth: 2 },
        }),
      ],
    });

    this.terraDraw.start();
    this.terraDraw.setMode('polygon');

    this.terraDraw.on('finish', (id) => {
      const feature = this.terraDraw.getSnapshot().find((f) => f.id === id);
      if (!feature) return;
      this._handleFinishedPolygon(feature);
    });
  }

  /**
   * Converts a finished Terra Draw polygon feature into a google.maps.Polygon
   * so the rest of the component (overlay list, click listeners, vertex export) is unaffected.
   */
  private _handleFinishedPolygon(feature: GeoJSONStoreFeatures) {
    const ring = (feature.geometry as any).coordinates[0] as number[][];
    const path = ring.map(([lng, lat]) => ({ lat, lng }));

    const polygonOverlay = new google.maps.Polygon({
      map: this._map,
      paths: path,
      strokeColor: '#000000',
      fillColor: '#000000',
      strokeWeight: 2,
      strokeOpacity: 0.8,
    });

    this._listenToPolygonClick(polygonOverlay);
    this._overlayList.push(polygonOverlay);

    // Remove the raw Terra Draw feature since we've replaced it with a real google.maps.Polygon
    this.terraDraw.removeFeatures([feature.id as string]);
  }

  startDrawing() {
    this.activeMode = 'draw';
    if (this.terraDraw) {
      this.terraDraw.setMode('polygon');
    }
  }
  enableSelectMode() {
    this.activeMode = 'select';
    if (this.terraDraw) {
      this.terraDraw.setMode('static');
    }
  }


  /**
   * Renders polygon overlays on map which are passed via Input or GeometryMapDialogData
   * @param multiPolygon List of polygon coordinates to be displayed
   */
  private _addMultiPolygonToMap(multiPolygon: MultiPolygon) {
    multiPolygon.forEach((polygon) => {
      const polygonOverlay: google.maps.Polygon = new google.maps.Polygon({
        map: this._map,
        paths: polygon,
        strokeColor: '#000000',
        fillColor: '#000000',
        strokeWeight: 2,
        strokeOpacity: 0.8,
      });

      if (!this.hideControls) this._listenToPolygonClick(polygonOverlay);

      this._overlayList.push(polygonOverlay);
    });
    this._setMultiPolygonBounds();
  }

  /**
   * Adds a click event listener to the polygon.
   * Helps add/remove the polygon from selected polygon list.
   * @param polygon Google map polygon overlay
   */
  private _listenToPolygonClick(polygon: google.maps.Polygon): void {
    google.maps.event.addListener(polygon, 'click', () => {
      if (this._selectedOverlayList?.includes(polygon)) {
        polygon.setOptions({
          fillColor: '#000000',
          strokeColor: '#000000',
        });
        this._selectedOverlayList = this._selectedOverlayList.filter((overlay) => {
          return overlay != polygon;
        });
      } else {
        polygon.setOptions({
          fillColor: '#2561d9',
          strokeColor: '#2561d9',
        });
        this._selectedOverlayList?.push(polygon);
      }
    });
  }

  /**
   * Updated the bounds of the map every time there is a change in `multiPolygon` Input.
   */
  private _setMultiPolygonBounds() {
    const bounds = new google.maps.LatLngBounds();

    // this._overlayList.forEach((overlay) => {
    //   (overlay as google.maps.Polygon).getPaths().forEach((path) => {
    //     path.forEach((point) => {
    //       bounds.extend(point);
    //     });
    //   });
    // });
    this._overlayList.forEach((overlay) => {
      if (overlay instanceof google.maps.Polygon) {
        overlay.getPaths().forEach(path => {
          path.getArray().forEach(point => bounds.extend(point));
        });
      }
      else if (overlay instanceof google.maps.Polyline) {
        overlay.getPath().getArray().forEach(point => bounds.extend(point));
      }
    });


    this._map.fitBounds(bounds);
    this._map.setZoom(this.zoom)
    // this._map.setZoom((this._map.getZoom() ?? 18) + 0.5);
  }

  /**
   * Converts a polygon layout into polygon coordinates.
   * @param polygon Google map polygon overlay
   * @returns Array of Polygon coordinates
   */
  private _getPolygonVertices(polygon: google.maps.Polygon): Polygon {
    const nodes = [];
    const vertices = polygon.getPath().getArray();
    for (let i = 0; i < vertices.length; i++) {
      nodes[i] = { lat: vertices[i].lat(), lng: vertices[i].lng() };
    }
    return nodes;
  }

  private _renderHtmlMarker(markerList?: MarkerCoordinate[]) {
    // Select all duplicate markers
    if (markerList) this._activeMarkerList = this._getRelatedMarkerList(markerList);

    // Create new markers and add them to the list
    markerList?.forEach((marker) => {
      // Check if a marker already exists at this lat/lng
      let htmlMarker = this._activeMarkerList?.find(
        (m) => m.getPosition().lat() === +marker.lat && m.getPosition().lng() === +marker.lng
      );
      if (!htmlMarker) {
        // Create new marker if none exists
        // htmlMarker = new GoogleHtmlMarker({ lat: +marker.lat, lng: +marker.lng } as google.maps.LatLngLiteral).setMap(
        //   this._map
        // );
        htmlMarker = new this.GoogleHtmlMarkerClass({
          lat: +marker.lat,
          lng: +marker.lng,
        } as google.maps.LatLngLiteral).setMap(this._map);
        // Initialize payload with the first property
        htmlMarker.setPayload({ propertyList: [marker] });
        this._activeMarkerList?.push(htmlMarker);
        this._listenToMarkerClick(htmlMarker);
      } else {
        // Marker exists → append property
        const payload = htmlMarker.getPayload() || {};
        payload.propertyList = [...(payload.propertyList || []), marker];
        htmlMarker.setPayload(payload);
      }
      // Update marker type
      htmlMarker.setType(marker.type);
      // Set label based on property count in payload
      const payload = htmlMarker.getPayload();
      const propertyCount = payload?.propertyList?.length || 1;
      htmlMarker.setLabel(propertyCount > 1 ? `${propertyCount} Nos` : marker.label);
      // Set selected
      htmlMarker.selected(marker.isSelected);
    });
  }

  private _getRelatedMarker(coordinate: MarkerCoordinate): GoogleHtmlMarker | undefined {
    if (!coordinate.lat || !coordinate.lng) return;

    return this._activeMarkerList?.find((marker) => {
      const markerCoordinate = marker.getPosition();
      return (
        markerCoordinate.lat() == coordinate.lat &&
        markerCoordinate.lng() == coordinate.lng &&
        marker.getPayload().id == coordinate.payload.id
      );
    });
  }

  private _getRelatedMarkerList(coordinateList: MarkerCoordinate[]): GoogleHtmlMarker[] {
    return (
      this._activeMarkerList?.filter((activeMarker) => {
        const markerCoordinate = activeMarker.getPosition();
        const relatedMarkerExists = coordinateList?.some((coordinate) => {
          return (
            markerCoordinate.lat() == coordinate.lat &&
            markerCoordinate.lng() == coordinate.lng &&
            activeMarker.getPayload().id == coordinate.payload.id
          );
        });

        if (!relatedMarkerExists) activeMarker.setMap(null);
        return relatedMarkerExists;
      }) ?? []
    );
  }

  private _listenToMarkerClick(marker: GoogleHtmlMarker): void {
    google.maps.event.addListener(marker, 'click', () => this._openInfoWindow(marker));
  }

  /**
   * @internal
   * Opens the info-window when clicked on a marker
   */
  private _openInfoWindow(marker: GoogleHtmlMarker): void {
    this._activeInfoWindow?.close();
    this._activeInfoWindow = new google.maps.InfoWindow({
      content: this._getInfoWindowContent(marker),
      pixelOffset: new google.maps.Size(0, -10),
    });
    this._setInfoWindowOffset(marker);
    this._listenToInfoWindow(this._activeInfoWindow, marker);
    this._activeInfoWindow.open(this._map, marker);
  }

  private _getInfoWindowContent(marker: GoogleHtmlMarker): string {
    const payload = marker.getPayload();
    if (payload?.propertyList?.length) {
      this.property = payload.propertyList.map(p =>
        this.opScanState.comparableList.find(c => c.index === p.payload.id)
      );
    }
    const template = this.property.length > 1 ? GoogleMapConfig.multiplePropertyBox : GoogleMapConfig.detailedInfoBox;
    const infoWindowContent = this.property
      .map(prop => this.buildPropertyBox(prop, template))
      .join('');
    return infoWindowContent;
  }

  private buildPropertyBox(prop: any, template: string): string {
    let box = template;

    box = box.replace(/#PMXPropertyId#/gi, prop?.PMXPropertyId || '');
    box = box.replace(/#index#/gi, prop?.index || '');
    box = box.replace(
      /#bedroomCount#/gi,
      this.toStringPipe.transform(prop?.BedroomsTotal) || '0'
    );
    box = box.replace(/#listingStatus#/gi, prop?.StandardStatus ?? '');
    box = box.replace(/#statusClass#/gi, this.getStatusClass(prop?.StandardStatus))
    box = box.replace(
      /#bathroomCount#/gi,
      this.toStringPipe.transform(this.roundUp(prop?.BathroomsDecimal)) || '0'
    );

    box = box.replace(
      /#livingArea#/gi,
      this.numberPipe.transform(
        this.property?.LivingArea
          ? this.property.LivingArea
          : this.property?.PublicRecord?.LivingArea
            ? this.property.PublicRecord.LivingArea
            : 0
      ) || '0'
    );

    box = box.replace(
      /#lotSizeSquareFeet#/gi,
      this.numberPipe.transform(
        prop?.LotSizeSquareFeet ? prop.LotSizeSquareFeet :
          prop?.PublicRecord?.LotSizeSquareFeet ? prop?.PublicRecord?.LotSizeSquareFeet :
            0
      )
    );
    box = box.replace(
      /#price#/gi,
      this.decimalPipe.transform(
        prop?.ListPrice ??
        prop?.ClosePrice ??
        0
      ) || '0'
    );
    if (prop?.ImageURLs?.length > 0) {
      box = box.replace(
        /#imageUrl#/gi,
        `${prop.ImageURLs[0]}&imageName=1&height=219`
      );
    } else if (prop?.ListingId) {
      box = box.replace(
        /#imageUrl#/gi,
        `${environment.listingImageBaseUrl}?ListingId=${prop.ListingId}&ImageName=1&height=180`
      );
    } else {
      box = box.replace(
        /#imageUrl#/gi,
        `${environment.listingImageBaseUrl}?ListingId=0&imageName=1&height=219`
      );
    }

    let fullAddress = prop?.Address || '';

    if (prop?.UnparsedAddress) {
      fullAddress = prop.UnparsedAddress || '';
      if (prop?.City) fullAddress += ', ' + prop.City;
      if (prop?.StateOrProvince) fullAddress += ', ' + prop.StateOrProvince;
      if (prop?.PostalCode) fullAddress += ' ' + prop.PostalCode;
    }

    box = box.replace(/#fullAddress#/gi, fullAddress);

    return box;
  }
  getStatusClass(status: string): string {
    if (!status) return '';

    if (status === 'Sold') return 'sold-status';

    if (
      status === 'Active' ||
      status === 'Active UC' ||
      status === 'Active Under Contract' ||
      status === 'Pending'
    ) return 'active-status';

    if (status === 'Cancel/Withdrawn') return 'cancel-status';

    if (status === 'Expired') return 'expired-status';

    return '';
  }

  roundUp(value: number | null | undefined): number | string {
    if (value === null || value === undefined) {
      return '--';
    }
    return Math.ceil(value);
  }

  private _setInfoWindowOffset(marker: GoogleHtmlMarker) {
    const center = this._map.getProjection()?.fromLatLngToPoint(this._map.getCenter() as google.maps.LatLng);
    const point = this._map.getProjection()?.fromLatLngToPoint(marker.getPosition());
    let quadrant = '';

    if (point && center) {
      quadrant += point.y > center.y ? 'b' : 't';
      quadrant += point.x < center.x ? 'l' : 'r';
    }

    document.getElementById('map')?.classList.remove('trWindow');
    document.getElementById('map')?.classList.remove('tlWindow');
    document.getElementById('map')?.classList.remove('brWindow');
    document.getElementById('map')?.classList.remove('blWindow');

    if (quadrant === 'tr') {
      document.getElementById('map')?.classList.add('trWindow');
    } else if (quadrant === 'tl') {
      document.getElementById('map')?.classList.add('tlWindow');
    } else if (quadrant === 'br') {
      document.getElementById('map')?.classList.add('brWindow');
    } else if (quadrant === 'bl') {
      document.getElementById('map')?.classList.add('blWindow');
    }
  }

  private _listenToInfoWindow(infoWindow: google.maps.InfoWindow, marker: GoogleHtmlMarker) {
    const comparable = this.opScanState.comparableList.find(
      (comparable) => comparable.index == marker.getPayload()?.id
    );

    google.maps.event.addListener(infoWindow, 'domready', () => {
      const infoBox = document.getElementById('pmx' + this.property?.PMXPropertyId);
      const selectButton = document.getElementById('selectButton');
      const deselectButton = document.getElementById('deselectButton');
      const detailButton = document.getElementById('detailButton');

      if (selectButton && comparable?.isSelectedComparable) selectButton.style.display = 'none';
      else if (deselectButton) deselectButton.style.display = 'none';

      selectButton?.addEventListener('click', () => {
        if (selectButton) selectButton.style.display = 'none';
        if (deselectButton) deselectButton.style.display = 'flex';
        this._propertySelected(marker.getPayload().id, true);
      });
      deselectButton?.addEventListener('click', () => {
        if (selectButton) selectButton.style.display = 'flex';
        if (deselectButton) deselectButton.style.display = 'none';
        this._propertySelected(marker.getPayload().id, false);
      });

      if (infoBox && this.isCMAEnabled) {
        infoBox.addEventListener('click', () => {
          this.workspaceService.openPropertyDetailsSheet(this.property);
        });
        detailButton?.addEventListener('click', () => this._propertyOpened(marker.getPayload().id));
      }
    });
  }

  private _propertySelected(id: string, isSelected: boolean) {
    // this.propertySelectionToggled.emit({ id, isSelected });
  }

  private _propertyOpened(id: string) {
    this.propertyOpened.emit(id);
  }

  openSnackbar(upgradeMsg, success = false) {
    this.upgradeSnackbar = this.snackbar.open(upgradeMsg, 'Dismiss', {
      duration: 5000,
      panelClass: [success ? 'snackbar-success' : 'snackbar-error'],
      verticalPosition: 'top',
    });
  }
}